import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { searchDocs, getRegions, getDocTypes, getAuthorities, getSearchStats, SearchFilters } from '@/services/api';

interface Region {
  code: string;
  name: string;
  count: number;
}

interface DocType {
  type: string;
  count: number;
}

interface Authority {
  authority: string;
  count: number;
}

interface SearchResult {
  id: string;
  eo_number: string;
  title: string;
  doc_type: string;
  authority: string;
  doc_date: string;
  status: string;
  doc_state?: string;
  region_code?: string;
  is_federal?: boolean;
  keywords?: string[];
  doc_number?: string;
  pages_count?: number;
  full_text?: string;
  highlights?: any;
}

// Читаем фильтры из URLSearchParams
function filtersFromParams(params: URLSearchParams): SearchFilters {
  const f: SearchFilters = {};
  if (params.get('type')) f.type = params.get('type')!;
  if (params.get('authority')) f.authority = params.get('authority')!;
  if (params.get('status')) f.status = params.get('status')!;
  if (params.get('region_code')) f.region_code = params.get('region_code')!;
  if (params.get('is_federal')) f.is_federal = params.get('is_federal') === 'true';
  if (params.get('doc_state')) f.doc_state = params.get('doc_state')!;
  if (params.get('date_from')) f.date_from = params.get('date_from')!;
  if (params.get('date_to')) f.date_to = params.get('date_to')!;
  if (params.get('keywords')) f.keywords = params.get('keywords')!;
  if (params.get('doc_number')) f.doc_number = params.get('doc_number')!;
  return f;
}

// Пишем фильтры в URLSearchParams
function paramsFromFiltersAndQuery(params: URLSearchParams, q: string, filters: SearchFilters) {
  const newParams = new URLSearchParams();
  if (q) newParams.set('q', q);
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      newParams.set(key, String(value));
    }
  });
  
  return newParams;
}

export const SearchPage: React.FC = () => {
  const [urlParams, setUrlParams] = useSearchParams();
  const q = urlParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(q);
  const [from, setFrom] = useState(0);
  const [sort, setSort] = useState('relevance');
  const [filters, setFilters] = useState<SearchFilters>(() => filtersFromParams(urlParams));
  const [showFilters, setShowFilters] = useState(true);
  
  // Флаг чтобы не зацикливаться
  const isUpdatingUrl = useRef(false);

  // Справочники
  const { data: regions } = useQuery('regions', getRegions, { staleTime: 5 * 60 * 1000 });
  const { data: docTypes } = useQuery('docTypes', getDocTypes, { staleTime: 5 * 60 * 1000 });
  const { data: authorities } = useQuery('authorities', getAuthorities, { staleTime: 5 * 60 * 1000 });
  const { data: stats } = useQuery('searchStats', getSearchStats, { staleTime: 60 * 1000 });

  // Синхронизация URL -> state (только при первой загрузке или внешнем изменении URL)
  useEffect(() => {
    if (isUpdatingUrl.current) return;
    const newQ = urlParams.get('q') || '';
    const newFilters = filtersFromParams(urlParams);
    setSearchQuery(newQ);
    setFilters(newFilters);
    setFrom(0);
  }, [urlParams.toString()]); // Сравниваем строку, а не объект

  // Поиск
  const { data, isLoading, error } = useQuery(
    ['search', q, filters, from, sort],
    () => searchDocs(q, filters, from, 20, sort),
    { keepPreviousData: true, enabled: true }
  );

  const results: SearchResult[] = data?.items || [];
  const total = data?.total || 0;

  // Обновление URL
  const updateUrl = useCallback((newQ: string, newFilters: SearchFilters) => {
    isUpdatingUrl.current = true;
    const newParams = paramsFromFiltersAndQuery(urlParams, newQ, newFilters);
    setUrlParams(newParams);
    setTimeout(() => { isUpdatingUrl.current = false; }, 50);
  }, [urlParams, setUrlParams]);

  const handleSearch = () => {
    setFrom(0);
    updateUrl(searchQuery.trim(), filters);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string | boolean | undefined) => {
    const next = { ...filters };
    if (value === undefined || value === '' || value === null) {
      delete next[key];
    } else {
      next[key] = value as any;
    }
    setFilters(next);
    setFrom(0);
    // Небольшая задержка чтобы state успел обновиться
    setTimeout(() => updateUrl(searchQuery, next), 0);
  };

  const handleSortChange = (newSort: string) => {
    setSort(newSort);
    setFrom(0);
  };

  const resetFilters = () => {
    setFilters({});
    setFrom(0);
    updateUrl(searchQuery, {});
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('ru-RU');
    } catch { return d; }
  };

  const statusLabel = (s?: string) => {
    const map: Record<string, string> = {
      active: 'Действует',
      amended: 'Изменён',
      cancelled: 'Отменён',
      repealed: 'Утратил силу',
      project: 'Проект',
      published: 'Опубликован'
    };
    return map[s || ''] || s || 'Неизвестно';
  };

  const statusClass = (s?: string) => {
    if (s === 'active' || s === 'published') return 'active';
    if (s === 'amended') return 'amended';
    if (s === 'cancelled' || s === 'repealed') return 'repealed';
    if (s === 'project') return 'project';
    return 'unknown';
  };

  const levelBadge = (doc: SearchResult) => {
    if (doc.is_federal) {
      return <span className="badge bg-primary me-1"><i className="bi bi-building"></i> Федеральный</span>;
    }
    if (doc.region_code) {
      const region = regions?.find((r: Region) => r.code === doc.region_code);
      return <span className="badge bg-info me-1"><i className="bi bi-geo-alt"></i> {region?.name || doc.region_code}</span>;
    }
    return null;
  };

  return (
    <div className="container-fluid py-4">
      {/* Статистика */}
      {stats && (
        <div className="row mb-4">
          <div className="col">
            <div className="card bg-gradient-primary text-white" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
              <div className="card-body d-flex justify-content-around text-center">
                <div>
                  <div className="h3 mb-0">{stats.total?.toLocaleString()}</div>
                  <small>Всего документов</small>
                </div>
                <div>
                  <div className="h3 mb-0">{stats.ocr_done?.toLocaleString()}</div>
                  <small>OCR завершён</small>
                </div>
                <div>
                  <div className="h3 mb-0">{stats.federal_count?.toLocaleString()}</div>
                  <small>Федеральных</small>
                </div>
                <div>
                  <div className="h3 mb-0">{stats.regional_count?.toLocaleString()}</div>
                  <small>Региональных</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <h4 className="mb-4 fw-bold"><i className="bi bi-search me-2"></i>Поиск документов</h4>

      {/* Поисковая строка */}
      <div className="search-box-lg mb-4">
        <i className="bi bi-search"></i>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Поиск по названию, номеру, органу, термину..."
        />
        <button onClick={handleSearch} disabled={isLoading}>
          {isLoading ? <span className="spinner-border spinner-border-sm" /> : 'Найти'}
        </button>
      </div>

      <div className="row g-3">
        {/* ФИЛЬТРЫ */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0"><i className="bi bi-funnel me-2"></i>Фильтры</h6>
              <button className="btn btn-sm btn-link text-decoration-none" onClick={() => setShowFilters(!showFilters)}>
                <i className={`bi bi-chevron-${showFilters ? 'up' : 'down'}`}></i>
              </button>
            </div>
            
            {showFilters && (
              <div className="card-body">
                {/* Уровень: Федеральный / Региональный */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Уровень</label>
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="level"
                      id="levelAll"
                      checked={filters.is_federal === undefined}
                      onChange={() => handleFilterChange('is_federal', undefined)}
                    />
                    <label className="btn btn-outline-secondary btn-sm" htmlFor="levelAll">Все</label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="level"
                      id="levelFederal"
                      checked={filters.is_federal === true}
                      onChange={() => handleFilterChange('is_federal', true)}
                    />
                    <label className="btn btn-outline-secondary btn-sm" htmlFor="levelFederal">Федеральные</label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="level"
                      id="levelRegional"
                      checked={filters.is_federal === false}
                      onChange={() => handleFilterChange('is_federal', false)}
                    />
                    <label className="btn btn-outline-secondary btn-sm" htmlFor="levelRegional">Региональные</label>
                  </div>
                </div>

                {/* Регион (только если выбраны региональные) */}
                {filters.is_federal === false && (
                  <div className="mb-3">
                    <label className="form-label small text-muted fw-medium">Регион</label>
                    <select
                      className="form-select form-select-sm"
                      value={filters.region_code || ''}
                      onChange={(e) => handleFilterChange('region_code', e.target.value || undefined)}
                    >
                      <option value="">Все регионы</option>
                      {regions?.map((r: Region) => (
                        <option key={r.code} value={r.code}>
                          {r.name} ({r.count})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Тип документа — динамический из API */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Тип документа</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.type || ''}
                    onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    {docTypes?.map((t: DocType) => (
                      <option key={t.type} value={t.type}>
                        {t.type} ({t.count})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Статус документа (doc_state) */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Статус документа</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.doc_state || ''}
                    onChange={(e) => handleFilterChange('doc_state', e.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    <option value="active">Действует</option>
                    <option value="cancelled">Отменён</option>
                    <option value="amended">Изменён</option>
                  </select>
                </div>

                {/* Орган власти — динамический из API */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Орган власти</label>
                  <input
                    type="text"
                    className="form-control form-select-sm"
                    list="authorityList"
                    placeholder="Начните вводить..."
                    value={filters.authority || ''}
                    onChange={(e) => handleFilterChange('authority', e.target.value || undefined)}
                  />
                  <datalist id="authorityList">
                    {authorities?.slice(0, 50).map((a: Authority) => (
                      <option key={a.authority} value={a.authority} />
                    ))}
                  </datalist>
                </div>

                {/* Номер документа */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Номер документа</label>
                  <input
                    type="text"
                    className="form-control form-select-sm"
                    placeholder="Например: 44-ФЗ"
                    value={filters.doc_number || ''}
                    onChange={(e) => handleFilterChange('doc_number', e.target.value || undefined)}
                  />
                </div>

                {/* Ключевые слова */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Ключевые слова</label>
                  <input
                    type="text"
                    className="form-control form-select-sm"
                    placeholder="Например: закупки"
                    value={filters.keywords || ''}
                    onChange={(e) => handleFilterChange('keywords', e.target.value || undefined)}
                  />
                </div>

                {/* Период */}
                <div className="mb-3">
                  <label className="form-label small text-muted fw-medium">Дата принятия</label>
                  <div className="row g-2">
                    <div className="col-6">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.date_from || ''}
                        onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
                      />
                    </div>
                    <div className="col-6">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.date_to || ''}
                        onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
                      />
                    </div>
                  </div>
                </div>

                <hr />
                
                <div className="d-grid gap-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={resetFilters}>
                    <i className="bi bi-x-circle me-1"></i>Сбросить фильтры
                  </button>
                  <button className="btn btn-sm btn-outline-primary">
                    <i className="bi bi-bookmark-plus me-1"></i>Сохранить как сегмент
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* РЕЗУЛЬТАТЫ */}
        <div className="col-lg-9">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small">
              Найдено: <strong className="text-dark">{total.toLocaleString()}</strong>
              {q && <span className="ms-2">по запросу «{q}»</span>}
            </span>
            <div className="d-flex gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
              >
                <option value="relevance">По релевантности</option>
                <option value="date_desc">Дата ↓</option>
                <option value="date_asc">Дата ↑</option>
              </select>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary active"><i className="bi bi-list"></i></button>
                <button className="btn btn-outline-secondary"><i className="bi bi-grid"></i></button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Загрузка...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Ошибка загрузки: {(error as any)?.message || 'Неизвестная ошибка'}
            </div>
          ) : (
            <div className="table-card">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Документ</th>
                      <th style={{ width: 120 }}>Тип / Уровень</th>
                      <th style={{ width: 150 }}>Орган</th>
                      <th style={{ width: 100 }}>Дата</th>
                      <th style={{ width: 100 }}>Статус</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                          По запросу ничего не найдено
                          <br />
                          <small>Попробуйте изменить фильтры или поисковый запрос</small>
                        </td>
                      </tr>
                    )}
                    {results.map((doc: SearchResult) => (
                      <tr key={doc.id}>
                        <td className="align-middle">
                          <button className="btn btn-sm btn-link text-warning p-0">
                            <i className="bi bi-star"></i>
                          </button>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <Link to={`/document/${doc.eo_number}`} className="text-decoration-none fw-medium">
                              {doc.title}
                            </Link>
                            <small className="text-muted font-monospace">{doc.eo_number}</small>
                            {doc.doc_number && (
                              <small className="text-muted">№ {doc.doc_number}</small>
                            )}
                            {doc.keywords && doc.keywords.length > 0 && (
                              <div className="mt-1">
                                {doc.keywords.slice(0, 3).map((k, i) => (
                                  <span key={i} className="badge bg-light text-dark border me-1" style={{ fontSize: '0.7rem' }}>
                                    {k}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex flex-column gap-1">
                            <span className="badge bg-secondary" style={{ fontSize: '0.75rem' }}>
                              {doc.doc_type || '—'}
                            </span>
                            {levelBadge(doc)}
                          </div>
                        </td>
                        <td className="align-middle small">{doc.authority || '—'}</td>
                        <td className="align-middle small">{formatDate(doc.doc_date)}</td>
                        <td className="align-middle">
                          <span className={`badge-status badge-${statusClass(doc.doc_state || doc.status)}`}>
                            {statusLabel(doc.doc_state || doc.status)}
                          </span>
                        </td>
                        <td className="align-middle">
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-light" title="Предпросмотр">
                              <i className="bi bi-eye"></i>
                            </button>
                            <button className="btn btn-light" title="Заметка">
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Пагинация */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">
              Показано {from + 1}–{Math.min(from + 20, total)} из {total.toLocaleString()}
            </span>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={from === 0}
                onClick={() => setFrom(Math.max(0, from - 20))}
              >
                <i className="bi bi-chevron-left"></i> Назад
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={from + 20 >= total}
                onClick={() => setFrom(from + 20)}
              >
                Вперёд <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
